import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 * 
 * Purpose: Automatically scrolls window to top on every route change.
 * Replicates Amazon-style SPA navigation UX.
 * 
 * Why this is needed:
 * React Router preserves scroll position by default when navigating between routes.
 * This is problematic in ecommerce apps where users expect to see the top of the page
 * (header, breadcrumbs, title) immediately after clicking a product or category link.
 * 
 * How it works:
 * - Listens to route changes via useLocation() hook
 * - On every pathname/search change, scrolls to top instantly
 * - Uses window.scrollTo(0, 0) for immediate scroll (not smooth)
 * - Runs in useEffect to ensure DOM is ready
 * 
 * Edge cases handled:
 * - Hash links (#section): Preserves default browser hash behavior
 * - Browser back/forward: Still scrolls to top (expected ecommerce behavior)
 * - Query params: Triggers scroll (e.g., /search?q=shoes → /search?q=laptop)
 * 
 * Does NOT interfere with:
 * - Modal/dialog scroll positions
 * - Internal page scrolling (carousels, tabs)
 * - Programmatic scrolling in components
 * 
 * Usage: Place once at top level of Router in App.js
 */
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Instant scroll to top on route change
    // Using instant scroll (not smooth) for immediate visual feedback
    // This matches Amazon, eBay, and other major ecommerce platforms
    window.scrollTo(0, 0);
  }, [pathname, search]); // Triggers on both path AND query param changes

  // This component renders nothing - it's purely for side effects
  return null;
};

export default ScrollToTop;
